import { Fragment } from 'react';
import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { IconButton, Icon, Tooltip, icon as iconSize } from '@nula/core-ui';
import { isInTable, TABLE_ACTIONS } from './table-actions';
import { EditorToolbar, EditorToolbarDivider } from './editor-toolbar';
import styles from './editor-table-menu.module.css';

export interface EditorTableMenuProps {
  editor: Editor;
}

interface TableButton {
  label: string;
  icon: string;
  /** Rotate the icon 90° (e.g. reuse delete_sweep for the column action). */
  rotated?: boolean;
  /** Destructive action — rendered in the error color. */
  danger?: boolean;
  run: (editor: Editor) => void;
}

/** Button groups separated by a divider in the toolbar (row | column | table). */
const BUTTON_GROUPS: TableButton[][] = [
  [
    { label: 'Insert row above', icon: 'vertical_align_top', run: TABLE_ACTIONS.insertRowAbove },
    { label: 'Insert row below', icon: 'vertical_align_bottom', run: TABLE_ACTIONS.insertRowBelow },
    { label: 'Delete row', icon: 'delete_sweep', danger: true, run: TABLE_ACTIONS.deleteRow },
  ],
  [
    { label: 'Insert column left', icon: 'first_page', run: TABLE_ACTIONS.insertColumnLeft },
    { label: 'Insert column right', icon: 'last_page', run: TABLE_ACTIONS.insertColumnRight },
    { label: 'Delete column', icon: 'delete_sweep', rotated: true, danger: true, run: TABLE_ACTIONS.deleteColumn },
  ],
  [{ label: 'Delete table', icon: 'delete', danger: true, run: TABLE_ACTIONS.deleteTable }],
];

/**
 * Contextual table controls — a bubble menu that appears whenever the selection
 * is inside a table, offering the row/column/table operations the legacy Lexical
 * context menu provided. Drives the pure {@link TABLE_ACTIONS} helpers (which
 * wrap TableKit's built-in commands). Each icon-only button gets a tooltip.
 */
export function EditorTableMenu({ editor }: EditorTableMenuProps) {
  const I = iconSize.sm;
  return (
    <BubbleMenu editor={editor} pluginKey="tableMenu" shouldShow={() => isInTable(editor)}>
      <EditorToolbar>
        {BUTTON_GROUPS.map((group, groupIndex) => (
          <Fragment key={group[0].label}>
            {groupIndex > 0 && <EditorToolbarDivider />}
            {group.map((btn) => (
              <Tooltip key={btn.label} title={btn.label} placement="top">
                <IconButton
                  size="sm"
                  transparent
                  aria-label={btn.label}
                  className={btn.danger ? styles.danger : undefined}
                  onClick={() => btn.run(editor)}
                >
                  <Icon name={btn.icon} size={I} className={btn.rotated ? styles.rotated90 : undefined} />
                </IconButton>
              </Tooltip>
            ))}
          </Fragment>
        ))}
      </EditorToolbar>
    </BubbleMenu>
  );
}
